import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Topbar from "../../components/common/Topbar";
import Toast from "../../components/common/Toast";
import { showToast } from "../../store/uiSlice";
import { setBalance } from "../../store/tradeSlice";
import { fmtUSD } from "../../utils/formatters";
import api from "../../services/api";

const UPI_1_ID = "bhavithran.r@ptyes  ";
const UPI_2_ID = "-----";

const Deposit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { balance } = useSelector((state) => state.trade);
  const [selectedMethod, setSelectedMethod] = useState("bank_transfer");
  const [amount, setAmount] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [depositHistory, setDepositHistory] = useState([]);
  const [stats, setStats] = useState({ totalDeposits: 0, depositCount: 0 });
  const [isVerifying, setIsVerifying] = useState(false);

  const [upiData, setUpiData] = useState(null);
  const [upiProofFile, setUpiProofFile] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isSubmittingProof, setIsSubmittingProof] = useState(false);

  const [bankScreenshot, setBankScreenshot] = useState(null);
  const [showBankDetails, setShowBankDetails] = useState(false);

  // UPI 1 manual states
  const [upi1Screenshot, setUpi1Screenshot] = useState(null);
  const [showUpi1Details, setShowUpi1Details] = useState(false);
  const [upi1Utr, setUpi1Utr] = useState("");
  const [upi1Note, setUpi1Note] = useState("");

  // UPI 2 manual states
  const [upi2Screenshot, setUpi2Screenshot] = useState(null);
  const [showUpi2Details, setShowUpi2Details] = useState(false);
  const [upi2Utr, setUpi2Utr] = useState("");
  const [upi2Note, setUpi2Note] = useState("");

  const isInrMethod = (method) =>
    ["wipays_upi", "upi1", "payalma", "upi2"].includes(method);

  useEffect(() => {
    fetchDepositHistory();
    const orderId = searchParams.get("order");
    const gateway = searchParams.get("gateway");
    const cancelled = searchParams.get("cancelled");
    const failed = searchParams.get("failed");
    if (cancelled) {
      dispatch(showToast({ message: "Payment was cancelled", type: "warn" }));
    } else if (failed) {
      dispatch(
        showToast({
          message: "Payment failed. Please try again.",
          type: "error",
        }),
      );
    } else if (orderId && gateway) {
      if (gateway === "wipays_upi") {
        pollUpiStatus(orderId);
      } else if (gateway === "bank_transfer" || gateway === "custom_upi") {
        dispatch(
          showToast({
            message: "Payment submitted! Admin will verify.",
            type: "success",
          }),
        );
        fetchDepositHistory();
      } else if (gateway === "payalma") {
        verifyPayalmaPayment(orderId);
      } else {
        verifyAndCompletePayment(orderId, gateway);
      }
    }
  }, [searchParams]);

  const fetchDepositHistory = async () => {
    try {
      const response = await api.get("/deposit/history");
      const allDeposits = response.data || [];
      const completedDeposits = allDeposits.filter(
        (d) => d.status === "completed",
      );
      setDepositHistory(completedDeposits);
      const total = completedDeposits.reduce(
        (sum, d) => sum + Number(d.amount || 0),
        0,
      );
      setStats({
        totalDeposits: total,
        depositCount: completedDeposits.length,
      });
    } catch (error) {
      console.error("Failed to fetch deposit history:", error);
    }
  };

  const verifyAndCompletePayment = async (orderId, gateway) => {
    setIsVerifying(true);
    try {
      const res = await api.get(
        `/deposit/verify?orderId=${orderId}&gateway=${gateway}`,
      );
      if (res.data.verified) {
        const balanceRes = await api.get("/user/balance");
        dispatch(setBalance(balanceRes.data.balance));
        dispatch(
          showToast({
            message: "✅ Deposit completed successfully!",
            type: "success",
          }),
        );
        fetchDepositHistory();
        setTimeout(() => navigate("/terminal"), 2000);
      } else {
        dispatch(
          showToast({
            message: "Payment is being processed. Funds will credit shortly.",
            type: "info",
          }),
        );
      }
    } catch (e) {
      dispatch(
        showToast({
          message: "Payment verification failed. Contact support.",
          type: "error",
        }),
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const verifyPayalmaPayment = async (orderId) => {
    setIsVerifying(true);
    try {
      const res = await api.get(
        `/deposit/verify?orderId=${orderId}&gateway=payalma`,
      );
      if (res.data.verified) {
        const balanceRes = await api.get("/user/balance");
        dispatch(setBalance(balanceRes.data.balance));
        dispatch(
          showToast({
            message: "✅ Deposit completed successfully!",
            type: "success",
          }),
        );
        fetchDepositHistory();
        setTimeout(() => navigate("/terminal"), 2000);
      } else {
        dispatch(
          showToast({
            message:
              "Payment received! Funds will credit shortly after verification.",
            type: "success",
          }),
        );
        fetchDepositHistory();
      }
    } catch (e) {
      dispatch(
        showToast({
          message: "Payment received! Funds will credit shortly.",
          type: "success",
        }),
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const pollUpiStatus = async (orderId) => {
    setIsPolling(true);
    let attempts = 0;
    const maxAttempts = 30;

    const poll = async () => {
      try {
        const res = await api.get(
          `/deposit/wipays/upi/status?orderId=${orderId}`,
        );
        if (res.data.verified) {
          const balanceRes = await api.get("/user/balance");
          dispatch(setBalance(balanceRes.data.balance));
          dispatch(
            showToast({
              message: "✅ UPI payment confirmed!",
              type: "success",
            }),
          );
          fetchDepositHistory();
          setIsPolling(false);
          setUpiData(null);
          setTimeout(() => navigate("/terminal"), 2000);
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000);
        } else {
          setIsPolling(false);
          dispatch(
            showToast({
              message:
                "Payment taking longer than expected. Submit proof if you have paid.",
              type: "info",
            }),
          );
        }
      } catch (e) {
        setIsPolling(false);
      }
    };

    setTimeout(poll, 5000);
  };

  const setPresetAmount = (preset) => setAmount(preset);

  // Unified manual UPI submit — used by both UPI 1 and UPI 2
  const submitManualUpi = async (upiId, screenshot, utr, note, resetFn) => {
    const amountNum = parseFloat(amount);
    if (!amountNum || amountNum < 100) {
      dispatch(
        showToast({ message: "Minimum deposit is ₹100", type: "error" }),
      );
      return;
    }
    if (amountNum > 1000000) {
      dispatch(
        showToast({ message: "Maximum deposit is 1,000,000", type: "error" }),
      );
      return;
    }
    if (!screenshot) {
      dispatch(
        showToast({
          message: "Please upload a screenshot of your UPI payment",
          type: "error",
        }),
      );
      return;
    }

    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append("amount", amountNum);
      formData.append("screenshot", screenshot);
      if (utr) formData.append("utrNumber", utr);
      if (note) formData.append("paymentNote", note);
      formData.append("upiId", upiId);

      const response = await api.post("/deposit/custom-upi", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        dispatch(
          showToast({
            message: "✅ UPI payment submitted! Admin will verify.",
            type: "success",
          }),
        );
        setAmount("");
        resetFn();
        fetchDepositHistory();
      } else {
        dispatch(
          showToast({
            message: response.data.message || "Failed to submit",
            type: "error",
          }),
        );
      }
    } catch (error) {
      dispatch(
        showToast({
          message:
            error.response?.data?.message ||
            "Deposit failed. Please try again.",
          type: "error",
        }),
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const processDeposit = async (gateway) => {
    // Bank Transfer
    if (gateway === "bank_transfer") {
      const amountNum = parseFloat(amount);
      if (!amountNum || amountNum < 50) {
        dispatch(
          showToast({ message: "Minimum deposit is $50", type: "error" }),
        );
        return;
      }
      if (amountNum > 1000000) {
        dispatch(
          showToast({
            message: "Maximum deposit is 1,000,000",
            type: "error",
          }),
        );
        return;
      }
      if (!bankScreenshot) {
        dispatch(
          showToast({
            message: "Please upload a screenshot of your transfer",
            type: "error",
          }),
        );
        return;
      }

      setIsProcessing(true);
      try {
        const formData = new FormData();
        formData.append("amount", amountNum);
        formData.append("screenshot", bankScreenshot);

        const response = await api.post("/deposit/bank-transfer", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.data.success) {
          dispatch(
            showToast({
              message: "✅ Bank transfer submitted! Admin will verify.",
              type: "success",
            }),
          );
          setAmount("");
          setBankScreenshot(null);
          setShowBankDetails(false);
          fetchDepositHistory();
        } else {
          dispatch(
            showToast({
              message: response.data.message || "Failed to submit",
              type: "error",
            }),
          );
        }
      } catch (error) {
        dispatch(
          showToast({
            message:
              error.response?.data?.message ||
              "Deposit failed. Please try again.",
            type: "error",
          }),
        );
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    // UPI 1 — manual upload with UPI_1_ID
    if (gateway === "upi1") {
      return submitManualUpi(UPI_1_ID, upi1Screenshot, upi1Utr, upi1Note, () => {
        setUpi1Screenshot(null);
        setShowUpi1Details(false);
        setUpi1Utr("");
        setUpi1Note("");
      });
    }

    // UPI 2 — manual upload with UPI_2_ID
    if (gateway === "upi2") {
      return submitManualUpi(UPI_2_ID, upi2Screenshot, upi2Utr, upi2Note, () => {
        setUpi2Screenshot(null);
        setShowUpi2Details(false);
        setUpi2Utr("");
        setUpi2Note("");
      });
    }

    // Gateway methods (wipays_upi, payalma)
    const amountNum = parseFloat(amount);
    const inr = isInrMethod(gateway);
    const minAmount = inr ? 100 : 50;

    if (!amountNum || amountNum < minAmount) {
      dispatch(
        showToast({
          message: `Minimum deposit is ${inr ? "₹100" : "$50"}`,
          type: "error",
        }),
      );
      return;
    }
    if (amountNum > 1000000) {
      dispatch(
        showToast({ message: "Maximum deposit is 1,000,000", type: "error" }),
      );
      return;
    }

    setIsProcessing(true);
    try {
      let endpoint;
      if (gateway === "wipays_upi") endpoint = "/deposit/wipays/upi";
      else endpoint = "/deposit/payalma";

      const response = await api.post(endpoint, { amount: amountNum });

      if (response.data.success) {
        if (gateway === "wipays_upi") {
          setUpiData({
            orderId: response.data.orderId,
            qrCode: response.data.qrCode,
            qrContent: response.data.qrContent,
            paymentLinks: response.data.paymentLinks,
          });
          setIsPolling(true);
          pollUpiStatus(response.data.orderId);
        } else {
          window.location.href = response.data.checkoutUrl;
        }
      } else {
        dispatch(
          showToast({
            message: response.data.message || "Failed to initiate payment",
            type: "error",
          }),
        );
      }
    } catch (error) {
      dispatch(
        showToast({
          message:
            error.response?.data?.message ||
            "Deposit failed. Please try again.",
          type: "error",
        }),
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBankScreenshotUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 10 * 1024 * 1024) {
      dispatch(
        showToast({ message: "File size must be under 10MB", type: "error" }),
      );
      return;
    }
    setBankScreenshot(file);
  };

  const handleUpi1ScreenshotUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 10 * 1024 * 1024) {
      dispatch(
        showToast({ message: "File size must be under 10MB", type: "error" }),
      );
      return;
    }
    setUpi1Screenshot(file);
  };

  const handleUpi2ScreenshotUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 10 * 1024 * 1024) {
      dispatch(
        showToast({ message: "File size must be under 10MB", type: "error" }),
      );
      return;
    }
    setUpi2Screenshot(file);
  };

  const handleProofUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 10 * 1024 * 1024) {
      dispatch(
        showToast({ message: "File size must be under 10MB", type: "error" }),
      );
      return;
    }
    setUpiProofFile(file);
  };

  const submitProof = async () => {
    if (!upiProofFile || !upiData?.orderId) {
      dispatch(
        showToast({
          message: "Please select a screenshot first",
          type: "error",
        }),
      );
      return;
    }

    setIsSubmittingProof(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        const res = await api.post("/deposit/wipays/upi/proof", {
          orderId: upiData.orderId,
          proofData: base64,
        });
        if (res.data.success) {
          dispatch(
            showToast({
              message:
                "Proof submitted! We will verify and confirm your payment.",
              type: "success",
            }),
          );
          setUpiProofFile(null);
        } else {
          dispatch(
            showToast({ message: "Failed to submit proof", type: "error" }),
          );
        }
        setIsSubmittingProof(false);
      };
      reader.onerror = () => {
        dispatch(showToast({ message: "Failed to read file", type: "error" }));
        setIsSubmittingProof(false);
      };
      reader.readAsDataURL(upiProofFile);
    } catch (error) {
      dispatch(showToast({ message: "Failed to submit proof", type: "error" }));
      setIsSubmittingProof(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      dispatch(showToast({ message: "UPI ID copied!", type: "success" }));
    });
  };

  // Payment methods — sequence: Bank Transfer → AnzoTrade Pay → UPI → UPI 1 → UPI 2
  const paymentMethods = [
    {
      id: "bank_transfer",
      name: "Bank Transfer",
      icon: "fa-building-columns",
      desc: "Manual transfer · Screenshot",
      color: "#8b5cf6",
      popular: true,
    },
    {
      id: "payalma",
      name: "AnzoTrade Pay",
      icon: "fa-wallet",
      desc: "UPI · IMPS · Cards (INR)",
      color: "#a78bfa",
      popular: false,
    },
    {
      id: "wipays_upi",
      name: "UPI",
      icon: "fa-qrcode",
      desc: "Google Pay · PhonePe · PayTM",
      color: "#059669",
      popular: false,
    },
    {
      id: "upi1",
      name: "UPI 1",
      icon: "fa-mobile-screen",
      desc: "Direct UPI Payment",
      color: "#10b981",
      popular: true,
    },
    {
      id: "upi2",
      name: "UPI 2",
      icon: "fa-mobile-screen",
      desc: "Direct UPI Payment",
      color: "#f59e0b",
      popular: true,
    },
  ];

  const presetAmounts = [5000, 10000, 15000, 20000];

  const currencySymbol = isInrMethod(selectedMethod) ? "₹" : "$";

  const cardStyle = {
    background: "var(--color-bgSurface)",
    border: "1px solid var(--color-border)",
  };
  const inputStyle = {
    background: "var(--color-bgInput)",
    border: "1px solid var(--color-borderMedium)",
    color: "var(--color-text)",
    outline: "none",
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--color-bg)" }}>
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div
          className="absolute w-[700px] h-[700px] rounded-full top-[-260px] right-[-200px] animate-float"
          style={{
            background:
              "radial-gradient(circle, rgba(46,54,66,0.08), transparent 65%)",
          }}
        />
        <div
          className="absolute w-[600px] h-[600px] rounded-full bottom-[-200px] left-[-160px] animate-float animation-delay-2000"
          style={{
            background:
              "radial-gradient(circle, rgba(139,92,246,0.05), transparent 65%)",
          }}
        />
      </div>
      <Topbar />

      {isVerifying && (
        <div
          className="fixed inset-0 z-[500] flex items-center justify-center"
          style={{ background: "var(--color-bgOverlay)" }}
        >
          <div className="rounded-2xl p-8 text-center" style={cardStyle}>
            <div
              className="w-14 h-14 border-2 rounded-full animate-spin mx-auto mb-4"
              style={{
                borderColor: "var(--color-orange)",
                borderTopColor: "transparent",
              }}
            />
            <div
              className="font-syne text-lg font-bold mb-2"
              style={{ color: "var(--color-text)" }}
            >
              Verifying Payment
            </div>
            <div
              className="text-sm"
              style={{ color: "var(--color-textSecondary)" }}
            >
              Please wait while we confirm your deposit...
            </div>
          </div>
        </div>
      )}

      <div className="relative z-10 pt-[52px] p-5 max-w-[800px] mx-auto">
        <div className="text-center mb-8 animate-slide-up">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-3"
            style={{
              background: "var(--color-orangeBg)",
              border: "1px solid var(--color-orangeBorder)",
            }}
          >
            <i
              className="fas fa-circle-plus text-[9px]"
              style={{ color: "var(--color-text)" }}
            />
            <span
              className="text-[9px] font-mono tracking-wide"
              style={{ color: "var(--color-text)" }}
            >
              FUNDS
            </span>
          </div>
          <h1
            className="font-syne text-3xl md:text-4xl font-extrabold mb-2"
            style={{ color: "var(--color-text)" }}
          >
            Fund Your{" "}
            <span style={{ color: "var(--color-text)" }}>Trading Account</span>
          </h1>
          <p
            className="text-sm"
            style={{ color: "var(--color-textSecondary)" }}
          >
            Choose a payment method to deposit instantly
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 animate-slide-up animation-delay-100">
          {[
            {
              label: "Balance",
              value: fmtUSD(balance),
              color: "var(--color-text)",
              bg: "var(--color-orangeBg)",
              border: "var(--color-orangeBorder)",
              icon: "fa-wallet",
            },
            {
              label: "Total Deposits",
              value: fmtUSD(stats.totalDeposits),
              color: "var(--color-green)",
              bg: "var(--color-greenBg)",
              border: "var(--color-greenBorder)",
              icon: "fa-arrow-trend-up",
            },
            {
              label: "Deposits",
              value: stats.depositCount,
              color: "var(--color-purpleLight)",
              bg: "var(--color-purpleBg)",
              border: "var(--color-purpleBorder)",
              icon: "fa-layer-group",
            },
          ].map((card) => (
            <div
              key={card.label}
              className="rounded-xl p-4 flex items-center gap-3"
              style={cardStyle}
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{
                  background: card.bg,
                  border: `1px solid ${card.border}`,
                  color: card.color,
                }}
              >
                <i className={`fas ${card.icon} text-sm`} />
              </div>
              <div>
                <div
                  className="text-[8px] font-mono tracking-wide uppercase"
                  style={{ color: "var(--color-textDim)" }}
                >
                  {card.label}
                </div>
                <div
                  className="font-syne text-xl font-extrabold"
                  style={{ color: card.color }}
                >
                  {card.value}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* UPI Payment Modal (Wipays only) */}
        {upiData && (
          <div
            className="rounded-xl p-6 mb-6 animate-slide-up"
            style={{ ...cardStyle, borderColor: "var(--color-greenBorder)" }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-xs"
                  style={{
                    background: "var(--color-greenBg)",
                    border: "1px solid var(--color-greenBorder)",
                    color: "var(--color-green)",
                  }}
                >
                  <i className="fas fa-mobile-screen" />
                </div>
                <div>
                  <div
                    className="font-syne text-sm font-extrabold"
                    style={{ color: "var(--color-text)" }}
                  >
                    UPI Payment
                  </div>
                  <div
                    className="text-[9px] font-mono"
                    style={{ color: "var(--color-textSecondary)" }}
                  >
                    Scan QR or use app link
                  </div>
                </div>
              </div>
              {isPolling && (
                <div
                  className="flex items-center gap-2"
                  style={{ color: "var(--color-green)" }}
                >
                  <div
                    className="w-3 h-3 border-2 rounded-full animate-spin"
                    style={{
                      borderColor: "var(--color-green)",
                      borderTopColor: "transparent",
                    }}
                  />
                  <span className="text-[9px] font-mono">
                    Waiting for payment...
                  </span>
                </div>
              )}
            </div>

            {upiData.qrCode && (
              <div className="flex justify-center mb-4">
                <div className="bg-white p-4 rounded-xl">
                  <img
                    src={`data:image/png;base64,${upiData.qrCode}`}
                    alt="UPI QR Code"
                    className="w-48 h-48"
                  />
                </div>
              </div>
            )}

            {upiData.qrContent && (
              <div className="mb-4">
                <div
                  className="flex items-center gap-2 rounded-lg p-3"
                  style={{
                    background: "var(--color-bgInput)",
                    border: "1px solid var(--color-borderMedium)",
                  }}
                >
                  <input
                    type="text"
                    value={upiData.qrContent}
                    readOnly
                    className="flex-1 bg-transparent font-mono text-[10px] outline-none truncate"
                    style={{ color: "var(--color-textMuted)" }}
                  />
                  <button
                    onClick={() => copyToClipboard(upiData.qrContent)}
                    className="px-3 py-1.5 rounded text-[9px] font-mono transition-all"
                    style={{
                      background: "var(--color-greenBg)",
                      border: "1px solid var(--color-greenBorder)",
                      color: "var(--color-green)",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background =
                        "rgba(16,185,129,0.2)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background =
                        "var(--color-greenBg)")
                    }
                  >
                    <i className="fas fa-copy mr-1" /> Copy
                  </button>
                </div>
              </div>
            )}

            {upiData.paymentLinks &&
              Object.keys(upiData.paymentLinks).length > 0 && (
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {upiData.paymentLinks.gpay && (
                    <a
                      href={upiData.paymentLinks.gpay}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 py-3 rounded-lg bg-white text-black font-medium text-xs hover:shadow-lg transition-all"
                    >
                      <i className="fab fa-google text-sm" /> Google Pay
                    </a>
                  )}
                  {upiData.paymentLinks.phonepe && (
                    <a
                      href={upiData.paymentLinks.phonepe}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 py-3 rounded-lg bg-[#5f259f] text-white font-medium text-xs hover:shadow-lg transition-all"
                    >
                      <i className="fas fa-mobile-screen text-sm" /> PhonePe
                    </a>
                  )}
                  {upiData.paymentLinks.paytm && (
                    <a
                      href={upiData.paymentLinks.paytm}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 py-3 rounded-lg bg-[#00b9f1] text-white font-medium text-xs hover:shadow-lg transition-all"
                    >
                      <i className="fas fa-wallet text-sm" /> PayTM
                    </a>
                  )}
                  {upiData.paymentLinks.upi && (
                    <a
                      href={upiData.paymentLinks.upi}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 py-3 rounded-lg bg-[#097969] text-white font-medium text-xs hover:shadow-lg transition-all"
                    >
                      <i className="fas fa-link text-sm" /> Other UPI App
                    </a>
                  )}
                </div>
              )}

            <div
              className="border-t pt-4"
              style={{ borderColor: "var(--color-borderMedium)" }}
            >
              <div
                className="text-[9px] font-mono mb-2"
                style={{ color: "var(--color-textSecondary)" }}
              >
                Payment not confirming? Submit proof of payment:
              </div>
              <div className="flex items-center gap-2">
                <label className="flex-1 cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProofUpload}
                    className="hidden"
                  />
                  <div
                    className="flex items-center gap-2 px-3 py-2 rounded-lg font-mono text-[10px] transition-all"
                    style={{
                      background: "var(--color-bgInput)",
                      border: "1px solid var(--color-borderMedium)",
                      color: "var(--color-textMuted)",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.borderColor =
                        "var(--color-greenBorder)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.borderColor =
                        "var(--color-borderMedium)")
                    }
                  >
                    <i
                      className="fas fa-upload"
                      style={{ color: "var(--color-green)" }}
                    />
                    {upiProofFile ? upiProofFile.name : "Choose screenshot..."}
                  </div>
                </label>
                <button
                  onClick={submitProof}
                  disabled={!upiProofFile || isSubmittingProof}
                  className="px-4 py-2 rounded-lg text-[10px] font-mono font-bold transition-all disabled:opacity-50"
                  style={{
                    background: "var(--color-greenBg)",
                    border: "1px solid var(--color-greenBorder)",
                    color: "var(--color-green)",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmittingProof)
                      e.currentTarget.style.background = "rgba(16,185,129,0.2)";
                  }}
                  onMouseLeave={(e) => {
                    if (!isSubmittingProof)
                      e.currentTarget.style.background = "var(--color-greenBg)";
                  }}
                >
                  {isSubmittingProof ? (
                    <span
                      className="inline-block w-3 h-3 border-2 rounded-full animate-spin"
                      style={{
                        borderColor: "var(--color-green)",
                        borderTopColor: "transparent",
                      }}
                    />
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </div>

            <button
              onClick={() => {
                setUpiData(null);
                setIsPolling(false);
              }}
              className="w-full mt-4 py-2 text-[9px] font-mono transition-all"
              style={{ color: "var(--color-textDim)" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = "var(--color-textSecondary)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = "var(--color-textDim)")
              }
            >
              Cancel UPI Payment
            </button>
          </div>
        )}

        {/* UPI 1 Details (manual) */}
        {showUpi1Details && selectedMethod === "upi1" && (
          <div
            className="rounded-xl p-6 mb-6 animate-slide-up"
            style={{ ...cardStyle, borderColor: "var(--color-greenBorder)" }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-xs"
                  style={{
                    background: "var(--color-greenBg)",
                    border: "1px solid var(--color-greenBorder)",
                    color: "var(--color-green)",
                  }}
                >
                  <i className="fas fa-mobile-screen" />
                </div>
                <div>
                  <div
                    className="font-syne text-sm font-extrabold"
                    style={{ color: "var(--color-text)" }}
                  >
                    UPI 1 Payment Details
                  </div>
                  <div
                    className="text-[9px] font-mono"
                    style={{ color: "var(--color-textSecondary)" }}
                  >
                    Pay directly to our UPI ID
                  </div>
                </div>
              </div>
            </div>

            <div
              className="rounded-xl p-4 mb-4"
              style={{
                background: "var(--color-bgInput)",
                border: "1px solid var(--color-borderStrong)",
              }}
            >
              <div
                className="text-[10px] font-mono mb-2"
                style={{ color: "var(--color-textSecondary)" }}
              >
                UPI ID:
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="text-lg font-syne font-extrabold"
                  style={{ color: "var(--color-text)" }}
                >
                  {UPI_1_ID}
                </span>
                <button
                  onClick={() => copyToClipboard(UPI_1_ID)}
                  className="px-3 py-1.5 rounded text-[9px] font-mono transition-all"
                  style={{
                    background: "var(--color-greenBg)",
                    border: "1px solid var(--color-greenBorder)",
                    color: "var(--color-green)",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "rgba(16,185,129,0.2)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "var(--color-greenBg)")
                  }
                >
                  <i className="fas fa-copy mr-1" /> Copy
                </button>
              </div>
            </div>

            <div
              className="text-[9px] font-mono mb-4"
              style={{ color: "var(--color-yellow)" }}
            >
              <i className="fas fa-exclamation-triangle mr-1" /> Steps: 1. Open
              any UPI app → 2. Send payment to above UPI ID → 3. Upload
              screenshot below
            </div>

            <div className="space-y-3 mb-4">
              <div>
                <div
                  className="text-[10px] font-mono font-bold mb-2"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  UTR Number (Optional)
                </div>
                <input
                  type="text"
                  value={upi1Utr}
                  onChange={(e) => setUpi1Utr(e.target.value)}
                  placeholder="Enter UTR/Reference number"
                  className="w-full rounded-lg px-3 py-2 font-mono text-[11px]"
                  style={inputStyle}
                  onFocus={(e) =>
                    (e.target.style.borderColor = "var(--color-green)")
                  }
                  onBlur={(e) =>
                    (e.target.style.borderColor = "var(--color-borderMedium)")
                  }
                />
              </div>
              <div>
                <div
                  className="text-[10px] font-mono font-bold mb-2"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  Payment Note (Optional)
                </div>
                <input
                  type="text"
                  value={upi1Note}
                  onChange={(e) => setUpi1Note(e.target.value)}
                  placeholder="Any additional information"
                  className="w-full rounded-lg px-3 py-2 font-mono text-[11px]"
                  style={inputStyle}
                  onFocus={(e) =>
                    (e.target.style.borderColor = "var(--color-green)")
                  }
                  onBlur={(e) =>
                    (e.target.style.borderColor = "var(--color-borderMedium)")
                  }
                />
              </div>
            </div>

            <div
              className="border-t pt-4"
              style={{ borderColor: "var(--color-borderMedium)" }}
            >
              <div
                className="text-[10px] font-mono font-bold mb-2"
                style={{ color: "var(--color-textSecondary)" }}
              >
                Upload Payment Screenshot *
              </div>
              <label className="flex cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUpi1ScreenshotUpload}
                  className="hidden"
                />
                <div
                  className="flex items-center gap-2 px-3 py-3 rounded-lg font-mono text-[10px] transition-all w-full"
                  style={{
                    background: "var(--color-bgInput)",
                    border: "1px solid var(--color-borderStrong)",
                    color: "var(--color-textSecondary)",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.borderColor =
                      "var(--color-greenBorder)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.borderColor =
                      "var(--color-borderStrong)")
                  }
                >
                  <i
                    className="fas fa-upload"
                    style={{ color: "var(--color-green)" }}
                  />
                  {upi1Screenshot
                    ? upi1Screenshot.name
                    : "Choose screenshot of UPI payment..."}
                </div>
              </label>
            </div>

            <button
              onClick={() => setShowUpi1Details(false)}
              className="w-full mt-4 py-2 text-[9px] font-mono transition-all"
              style={{ color: "var(--color-textSecondary)" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = "var(--color-text)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = "var(--color-textSecondary)")
              }
            >
              Cancel UPI 1
            </button>
          </div>
        )}

        {/* UPI 2 Details (manual) */}
        {showUpi2Details && selectedMethod === "upi2" && (
          <div
            className="rounded-xl p-6 mb-6 animate-slide-up"
            style={{ ...cardStyle, borderColor: "var(--color-yellowBorder)" }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-xs"
                  style={{
                    background: "var(--color-yellowBg)",
                    border: "1px solid var(--color-yellowBorder)",
                    color: "var(--color-yellow)",
                  }}
                >
                  <i className="fas fa-mobile-screen" />
                </div>
                <div>
                  <div
                    className="font-syne text-sm font-extrabold"
                    style={{ color: "var(--color-text)" }}
                  >
                    UPI 2 Payment Details
                  </div>
                  <div
                    className="text-[9px] font-mono"
                    style={{ color: "var(--color-textSecondary)" }}
                  >
                    Pay directly to our UPI ID
                  </div>
                </div>
              </div>
            </div>

            <div
              className="rounded-xl p-4 mb-4"
              style={{
                background: "var(--color-bgInput)",
                border: "1px solid var(--color-borderStrong)",
              }}
            >
              <div
                className="text-[10px] font-mono mb-2"
                style={{ color: "var(--color-textSecondary)" }}
              >
                UPI ID:
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="text-lg font-syne font-extrabold"
                  style={{ color: "var(--color-text)" }}
                >
                  {UPI_2_ID}
                </span>
                <button
                  onClick={() => copyToClipboard(UPI_2_ID)}
                  className="px-3 py-1.5 rounded text-[9px] font-mono transition-all"
                  style={{
                    background: "var(--color-yellowBg)",
                    border: "1px solid var(--color-yellowBorder)",
                    color: "var(--color-yellow)",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "rgba(245,158,11,0.2)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background =
                      "var(--color-yellowBg)")
                  }
                >
                  <i className="fas fa-copy mr-1" /> Copy
                </button>
              </div>
            </div>

            <div
              className="text-[9px] font-mono mb-4"
              style={{ color: "var(--color-yellow)" }}
            >
              <i className="fas fa-exclamation-triangle mr-1" /> Steps: 1. Open
              any UPI app → 2. Send payment to above UPI ID → 3. Upload
              screenshot below
            </div>

            <div className="space-y-3 mb-4">
              <div>
                <div
                  className="text-[10px] font-mono font-bold mb-2"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  UTR Number (Optional)
                </div>
                <input
                  type="text"
                  value={upi2Utr}
                  onChange={(e) => setUpi2Utr(e.target.value)}
                  placeholder="Enter UTR/Reference number"
                  className="w-full rounded-lg px-3 py-2 font-mono text-[11px]"
                  style={inputStyle}
                  onFocus={(e) =>
                    (e.target.style.borderColor = "var(--color-yellow)")
                  }
                  onBlur={(e) =>
                    (e.target.style.borderColor = "var(--color-borderMedium)")
                  }
                />
              </div>
              <div>
                <div
                  className="text-[10px] font-mono font-bold mb-2"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  Payment Note (Optional)
                </div>
                <input
                  type="text"
                  value={upi2Note}
                  onChange={(e) => setUpi2Note(e.target.value)}
                  placeholder="Any additional information"
                  className="w-full rounded-lg px-3 py-2 font-mono text-[11px]"
                  style={inputStyle}
                  onFocus={(e) =>
                    (e.target.style.borderColor = "var(--color-yellow)")
                  }
                  onBlur={(e) =>
                    (e.target.style.borderColor = "var(--color-borderMedium)")
                  }
                />
              </div>
            </div>

            <div
              className="border-t pt-4"
              style={{ borderColor: "var(--color-borderMedium)" }}
            >
              <div
                className="text-[10px] font-mono font-bold mb-2"
                style={{ color: "var(--color-textSecondary)" }}
              >
                Upload Payment Screenshot *
              </div>
              <label className="flex cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUpi2ScreenshotUpload}
                  className="hidden"
                />
                <div
                  className="flex items-center gap-2 px-3 py-3 rounded-lg font-mono text-[10px] transition-all w-full"
                  style={{
                    background: "var(--color-bgInput)",
                    border: "1px solid var(--color-borderStrong)",
                    color: "var(--color-textSecondary)",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.borderColor =
                      "var(--color-yellowBorder)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.borderColor =
                      "var(--color-borderStrong)")
                  }
                >
                  <i
                    className="fas fa-upload"
                    style={{ color: "var(--color-yellow)" }}
                  />
                  {upi2Screenshot
                    ? upi2Screenshot.name
                    : "Choose screenshot of UPI payment..."}
                </div>
              </label>
            </div>

            <button
              onClick={() => setShowUpi2Details(false)}
              className="w-full mt-4 py-2 text-[9px] font-mono transition-all"
              style={{ color: "var(--color-textSecondary)" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = "var(--color-text)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = "var(--color-textSecondary)")
              }
            >
              Cancel UPI 2
            </button>
          </div>
        )}

        {/* Bank Transfer Details */}
        {showBankDetails && selectedMethod === "bank_transfer" && (
          <div
            className="rounded-xl p-6 mb-6 animate-slide-up"
            style={{ ...cardStyle, borderColor: "var(--color-purpleBorder)" }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-xs"
                  style={{
                    background: "var(--color-purpleBg)",
                    border: "1px solid var(--color-purpleBorder)",
                    color: "var(--color-purple)",
                  }}
                >
                  <i className="fas fa-building-columns" />
                </div>
                <div>
                  <div
                    className="font-syne text-sm font-extrabold"
                    style={{ color: "var(--color-text)" }}
                  >
                    Bank Transfer Details
                  </div>
                  <div
                    className="text-[9px] font-mono"
                    style={{ color: "var(--color-textSecondary)" }}
                  >
                    Transfer to the account below and upload screenshot
                  </div>
                </div>
              </div>
            </div>

            <div
              className="rounded-xl p-4 space-y-3 mb-4"
              style={{
                background: "var(--color-bgInput)",
                border: "1px solid var(--color-borderStrong)",
              }}
            >
              {[
                { label: "Account Number", value: "104601000004133" },
                { label: "IFSC Code", value: "IOBA0001046" },
                { label: "Account Name", value: "R E BHAVITHRAN" },
                { label: "Bank", value: "INDIAN OVERSEAS BANK" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex justify-between items-center"
                >
                  <span
                    className="text-[10px] font-mono"
                    style={{ color: "var(--color-textSecondary)" }}
                  >
                    {item.label}
                  </span>
                  <span
                    className="text-[11px] font-mono font-bold"
                    style={{ color: "var(--color-text)" }}
                  >
                    {item.value}
                  </span>
                </div>
              ))}
            </div>

            <div
              className="text-[9px] font-mono mb-3"
              style={{ color: "var(--color-yellow)" }}
            >
              <i className="fas fa-exclamation-triangle mr-1" /> After transfer,
              upload screenshot below and submit
            </div>

            <div
              className="border-t pt-4"
              style={{ borderColor: "var(--color-borderMedium)" }}
            >
              <div
                className="text-[10px] font-mono font-bold mb-2"
                style={{ color: "var(--color-textSecondary)" }}
              >
                Upload Transfer Screenshot *
              </div>
              <label className="flex cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBankScreenshotUpload}
                  className="hidden"
                />
                <div
                  className="flex items-center gap-2 px-3 py-3 rounded-lg font-mono text-[10px] transition-all w-full"
                  style={{
                    background: "var(--color-bgInput)",
                    border: "1px solid var(--color-borderStrong)",
                    color: "var(--color-textSecondary)",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.borderColor =
                      "var(--color-purpleBorder)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.borderColor =
                      "var(--color-borderStrong)")
                  }
                >
                  <i
                    className="fas fa-upload"
                    style={{ color: "var(--color-purple)" }}
                  />
                  {bankScreenshot
                    ? bankScreenshot.name
                    : "Choose screenshot of transfer..."}
                </div>
              </label>
            </div>

            <button
              onClick={() => setShowBankDetails(false)}
              className="w-full mt-4 py-2 text-[9px] font-mono transition-all"
              style={{ color: "var(--color-textSecondary)" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = "var(--color-text)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = "var(--color-textSecondary)")
              }
            >
              Cancel Bank Transfer
            </button>
          </div>
        )}

        {/* Payment Method Selection */}
        <div className="space-y-6">
          <div
            className="rounded-xl overflow-hidden animate-slide-up animation-delay-200"
            style={cardStyle}
          >
            <div
              className="px-5 py-4"
              style={{ borderBottom: "1px solid var(--color-borderMedium)" }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{
                    background: "var(--color-orangeBg)",
                    border: "1px solid var(--color-orangeBorder)",
                    color: "var(--color-text)",
                  }}
                >
                  <i className="fas fa-credit-card text-xs" />
                </div>
                <div>
                  <div
                    className="font-syne text-sm font-extrabold"
                    style={{ color: "var(--color-text)" }}
                  >
                    Payment Method
                  </div>
                  <div
                    className="text-[9px] font-mono"
                    style={{ color: "var(--color-textDim)" }}
                  >
                    Select your preferred payment provider
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {paymentMethods.map((method) => (
                  <button
                    key={method.id}
                    onClick={() => {
                      setSelectedMethod(method.id);
                      if (method.id === "bank_transfer") {
                        setShowBankDetails(true);
                        setShowUpi1Details(false);
                        setShowUpi2Details(false);
                      } else if (method.id === "upi1") {
                        setShowUpi1Details(true);
                        setShowUpi2Details(false);
                        setShowBankDetails(false);
                      } else if (method.id === "upi2") {
                        setShowUpi2Details(true);
                        setShowUpi1Details(false);
                        setShowBankDetails(false);
                      } else {
                        setShowBankDetails(false);
                        setShowUpi1Details(false);
                        setShowUpi2Details(false);
                      }
                    }}
                    className="relative p-4 rounded-xl text-center transition-all border"
                    style={{
                      background:
                        selectedMethod === method.id
                          ? "var(--color-orangeBg)"
                          : "var(--color-bgCard)",
                      borderColor:
                        selectedMethod === method.id
                          ? "var(--color-orange)"
                          : "var(--color-borderMedium)",
                    }}
                  >
                    {method.popular && (
                      <span
                        className="absolute top-2 right-2 text-[7px] px-1.5 py-0.5 rounded font-mono font-bold"
                        style={{
                          background: "var(--color-greenBg)",
                          color: "var(--color-green)",
                          border: "1px solid var(--color-greenBorder)",
                        }}
                      >
                        POPULAR
                      </span>
                    )}
                    <div
                      className="w-10 h-10 mx-auto rounded-xl flex items-center justify-center mb-2"
                      style={{
                        background: "var(--color-bgCard)",
                        border: "1px solid var(--color-borderMedium)",
                        color: method.color,
                      }}
                    >
                      <i className={`fas ${method.icon} text-lg`} />
                    </div>
                    <div
                      className="font-syne text-xs font-bold"
                      style={{ color: "var(--color-text)" }}
                    >
                      {method.name}
                    </div>
                    <div
                      className="text-[8px] font-mono mt-1"
                      style={{ color: "var(--color-textSecondary)" }}
                    >
                      {method.desc}
                    </div>
                    {selectedMethod === method.id && (
                      <div
                        className="mt-2 text-[8px] font-mono flex items-center justify-center gap-1"
                        style={{ color: "var(--color-text)" }}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ background: "var(--color-text)" }}
                        />{" "}
                        Selected
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Amount Input for non-bank-transfer and non-manual-UPI */}
          {selectedMethod !== "bank_transfer" &&
            selectedMethod !== "upi2" &&
            selectedMethod !== "upi1" && (
              <div className="rounded-xl overflow-hidden" style={cardStyle}>
                <div
                  className="px-5 py-4"
                  style={{
                    borderBottom: "1px solid var(--color-borderMedium)",
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{
                        background: "var(--color-orangeBg)",
                        border: "1px solid var(--color-orangeBorder)",
                        color: "var(--color-text)",
                      }}
                    >
                      <i className="fas fa-dollar-sign text-xs" />
                    </div>
                    <div>
                      <div
                        className="font-syne text-sm font-extrabold"
                        style={{ color: "var(--color-text)" }}
                      >
                        Deposit Amount
                      </div>
                      <div
                        className="text-[9px] font-mono"
                        style={{ color: "var(--color-textDim)" }}
                      >
                        Minimum {currencySymbol}
                        {isInrMethod(selectedMethod) ? "100" : "50"} · Zero fees
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {presetAmounts.map((preset) => (
                      <button
                        key={preset}
                        onClick={() => setPresetAmount(preset)}
                        className="py-2 rounded-lg font-mono text-[11px] font-semibold transition-all border"
                        style={{
                          background: "var(--color-bgCard)",
                          borderColor: "var(--color-borderMedium)",
                          color: "var(--color-textMuted)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor =
                            "var(--color-orange)";
                          e.currentTarget.style.color = "var(--color-text)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor =
                            "var(--color-borderMedium)";
                          e.currentTarget.style.color =
                            "var(--color-textMuted)";
                        }}
                      >
                        {currencySymbol}
                        {preset.toLocaleString()}
                      </button>
                    ))}
                  </div>
                  <div className="relative">
                    <span
                      className="absolute left-3 top-1/2 -translate-y-1/2 font-syne text-base font-extrabold"
                      style={{ color: "var(--color-text)" }}
                    >
                      {currencySymbol}
                    </span>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      min={isInrMethod(selectedMethod) ? 100 : 50}
                      step="1"
                      className="w-full rounded-lg px-3 py-3 pl-7 font-dm text-sm"
                      style={inputStyle}
                      onFocus={(e) =>
                        (e.target.style.borderColor = "var(--color-orange)")
                      }
                      onBlur={(e) =>
                        (e.target.style.borderColor =
                          "var(--color-borderMedium)")
                      }
                    />
                  </div>
                  <div
                    className="text-[8px] font-mono mt-2"
                    style={{
                      color:
                        amount &&
                        amount >= (isInrMethod(selectedMethod) ? 100 : 50)
                          ? "var(--color-green)"
                          : "var(--color-textDim)",
                    }}
                  >
                    {amount &&
                    amount >= (isInrMethod(selectedMethod) ? 100 : 50)
                      ? `You will deposit exactly ${currencySymbol}${parseFloat(amount).toLocaleString()}`
                      : `Enter an amount between ${currencySymbol}${isInrMethod(selectedMethod) ? "100" : "50"} and ${currencySymbol}1,000,000`}
                  </div>
                </div>
              </div>
            )}

          {/* Amount Input for Bank Transfer, UPI 1 and UPI 2 */}
          {(selectedMethod === "bank_transfer" ||
            selectedMethod === "upi2" ||
            selectedMethod === "upi1") && (
            <div className="rounded-xl overflow-hidden" style={cardStyle}>
              <div
                className="px-5 py-4"
                style={{ borderBottom: "1px solid var(--color-borderMedium)" }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{
                      background: "var(--color-orangeBg)",
                      border: "1px solid var(--color-orangeBorder)",
                      color: "var(--color-text)",
                    }}
                  >
                    <i className="fas fa-dollar-sign text-xs" />
                  </div>
                  <div>
                    <div
                      className="font-syne text-sm font-extrabold"
                      style={{ color: "var(--color-text)" }}
                    >
                      {selectedMethod === "upi2"
                        ? "UPI 2 Amount"
                        : selectedMethod === "upi1"
                          ? "UPI 1 Amount"
                          : "Transfer Amount"}
                    </div>
                    <div
                      className="text-[9px] font-mono"
                      style={{ color: "var(--color-textDim)" }}
                    >
                      {selectedMethod === "bank_transfer"
                        ? "Minimum $50 · Admin will verify"
                        : "Minimum ₹100 · Admin will verify"}
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {[5000, 10000, 15000, 20000].map((preset) => (
                    <button
                      key={preset}
                      onClick={() => setPresetAmount(preset)}
                      className="py-2 rounded-lg font-mono text-[11px] font-semibold transition-all border"
                      style={{
                        background: "var(--color-bgCard)",
                        borderColor: "var(--color-borderMedium)",
                        color: "var(--color-textMuted)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor =
                          "var(--color-orange)";
                        e.currentTarget.style.color = "var(--color-text)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor =
                          "var(--color-borderMedium)";
                        e.currentTarget.style.color = "var(--color-textMuted)";
                      }}
                    >
                      {selectedMethod === "bank_transfer" ? "$" : "₹"}
                      {preset.toLocaleString()}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <span
                    className="absolute left-3 top-1/2 -translate-y-1/2 font-syne text-base font-extrabold"
                    style={{ color: "var(--color-text)" }}
                  >
                    {selectedMethod === "bank_transfer" ? "$" : "₹"}
                  </span>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    min={selectedMethod === "bank_transfer" ? 50 : 100}
                    step="1"
                    className="w-full rounded-lg px-3 py-3 pl-7 font-dm text-sm"
                    style={inputStyle}
                    onFocus={(e) =>
                      (e.target.style.borderColor = "var(--color-orange)")
                    }
                    onBlur={(e) =>
                      (e.target.style.borderColor = "var(--color-borderMedium)")
                    }
                  />
                </div>
              </div>
            </div>
          )}

          {/* Pay Button */}
          <button
            onClick={() => processDeposit(selectedMethod)}
            disabled={isProcessing}
            className="w-full flex items-center justify-center gap-2 py-4 rounded-full font-syne font-bold text-sm transition-all disabled:opacity-50"
            style={{
              background: "var(--color-gradientOrange)",
              color: "var(--color-textInverse)",
            }}
          >
            {isProcessing ? (
              <span
                className="inline-block w-4 h-4 border-2 rounded-full animate-spin"
                style={{
                  borderColor: "var(--color-textInverse)",
                  borderTopColor: "transparent",
                }}
              />
            ) : (
              <>
                <i
                  className={`fas ${selectedMethod === "bank_transfer" ? "fa-building-columns" : selectedMethod === "upi2" || selectedMethod === "upi1" ? "fa-mobile-screen" : isInrMethod(selectedMethod) ? "fa-mobile-screen" : "fa-credit-card"} mr-1`}
                />
                {selectedMethod === "bank_transfer"
                  ? "Submit Bank Transfer"
                  : selectedMethod === "upi2"
                    ? "Submit UPI 2 Payment"
                    : selectedMethod === "upi1"
                      ? "Submit UPI 1 Payment"
                      : `Pay with ${paymentMethods.find((m) => m.id === selectedMethod)?.name}`}
              </>
            )}
          </button>

          {/* Deposit History */}
          <div className="rounded-xl overflow-hidden" style={cardStyle}>
            <div
              className="px-4 py-3"
              style={{ borderBottom: "1px solid var(--color-borderMedium)" }}
            >
              <div className="flex items-center gap-2">
                <i
                  className="fas fa-clock-rotate-left text-xs"
                  style={{ color: "var(--color-green)" }}
                />
                <div
                  className="font-syne text-xs font-extrabold"
                  style={{ color: "var(--color-text)" }}
                >
                  Deposit History
                </div>
                <span
                  className="text-[9px] font-mono"
                  style={{ color: "var(--color-textDim)" }}
                >
                  ({depositHistory.length} completed)
                </span>
              </div>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {depositHistory.length === 0 ? (
                <div className="text-center py-8">
                  <i
                    className="fas fa-inbox text-2xl mb-2 opacity-50"
                    style={{ color: "var(--color-textDim)" }}
                  />
                  <p
                    className="text-[9px] font-mono"
                    style={{ color: "var(--color-textDim)" }}
                  >
                    No completed deposits yet
                  </p>
                </div>
              ) : (
                depositHistory.map((deposit, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3"
                    style={{
                      borderBottom: "1px solid var(--color-borderLight)",
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-7 h-7 rounded-md flex items-center justify-center"
                        style={{
                          background: "var(--color-greenBg)",
                          border: "1px solid var(--color-greenBorder)",
                          color: "var(--color-green)",
                        }}
                      >
                        <i className="fas fa-circle-plus text-[9px]" />
                      </div>
                      <div>
                        <div
                          className="font-syne text-[10px] font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {deposit.method === "bank_transfer"
                            ? "Bank Transfer"
                            : deposit.method === "custom_upi"
                              ? "UPI 2"
                              : deposit.method === "wipays_upi"
                                ? "UPI (Wipays)"
                                : deposit.method === "upi1"
                                  ? "UPI 1"
                                  : deposit.method === "payalma"
                                    ? "AnzoTrade Pay"
                                    : deposit.method === "wipays"
                                      ? "Card Payment"
                                      : deposit.method || "Deposit"}
                        </div>
                        <div
                          className="text-[7px] font-mono"
                          style={{ color: "var(--color-textDim)" }}
                        >
                          {new Date(deposit.time).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className="font-mono text-[11px] font-bold"
                        style={{ color: "var(--color-green)" }}
                      >
                        +{fmtUSD(deposit.amount)}
                      </div>
                      <div
                        className="text-[7px] font-mono"
                        style={{ color: "var(--color-textDim)" }}
                      >
                        {deposit.status}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      <Toast />
      <style>{`
        @keyframes float{0%,100%{transform:translate(0,0)}50%{transform:translate(28px,-28px)}}
        @keyframes slide-up{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        .animate-float{animation:float 20s ease-in-out infinite}
        .animation-delay-2000{animation-delay:-8s}
        .animate-slide-up{animation:slide-up .6s ease-out both}
        .animation-delay-100{animation-delay:.1s}
        .animation-delay-200{animation-delay:.2s}
      `}</style>
    </div>
  );
};

export default Deposit;